<template>
    <div class="wy-auto-qa-ai-evaluation-question-manager-page">
      <div v-if="isPopupVisible" class="popup-wrapper">
        <div class="popup-body">
          <div class="title">Confirm</div>
          <div class="desc">{{ popupBodyText }}</div>
          <div class="footer">
            <el-button type="primary" size="mini" class="no-action" @click="isPopupVisible = false">NO</el-button>
            <el-button type="primary" size="mini" @click="handlePopupConfirm">YES</el-button>
          </div>
        </div>
      </div>
  
      <div class="header">
        <div class="title">Manage Custom Questions</div>
        <div class="new-form-btns">
          <el-button data-testid="autoQM-customerQuestionManager-backToFormManagerButton" type="primary" size="mini" class="back-to-form-btn" @click="handleBackToFormManager">
            <span class="back-to-form-btn-text">Back to form manager</span>
          </el-button>
          <el-button v-if="isRoleAllow(4707)" data-testid="autoQM-customerQuestionManager-newQuestionButton" type="primary" size="mini" class="new-question-btn" @click="handleCreateNewQuestion">
            <span class="new-question-btn-text">New question</span>
          </el-button>
        </div>
      </div>
      <div class="body">
        <div class="filter-row">
          <div class="results-wrapper">
            <div class="dropdown-wrapper">
              <span>Results per page</span>
              <el-select v-model="selectedPageSize" data-testid="autoQM-customerQuestionManager-resultPageSizeDropDown" :placeholder="String(selectedPageSize)" popper-class="question-manager-page-size-popper" @visible-change="rotateCaret">
                <el-option
                  v-for="sizeOption in pageSizeOptions"
                  :key="sizeOption.value"
                  :label="sizeOption.label"
                  :value="sizeOption.value"
                  @click.native="handlePageSizeChange(sizeOption.value)"
                ></el-option>
              </el-select>
            </div>
            <div class="pagination-size">
              {{ Math.min((currentPage - 1) * selectedPageSize + 1, paginationTotal) }} - {{ Math.min(this.currentPage * selectedPageSize, paginationTotal) }} of {{ paginationTotal }}
            </div>
            <div class="block">
              <el-pagination
                @current-change="handleCurrentChange"
                data-testid="autoQM-customerQuestionManager-paginationChangePageButton"
                :current-page.sync="currentPage"
                :page-sizes="pageSizeOptions.map((option) => option.value)"
                :page-size="selectedPageSize"
                layout="prev, next"
                :total="paginationTotal"
                :disabled="tableLoading"
              />
            </div>
          </div>
        </div>
        <el-table
          v-loading="tableLoading"
          row-class-name="no-hover"
          ref="filterTable"
          :data="tableData"
          :default-sort="{ prop: sortedColumn, order: sortOrder }"
          :header-cell-class-name="getHeaderClass"
          @sort-change="handleSortChange"
          @row-dblclick="handleDoubleRowClick"
          style="width: 100%; height: calc(100vh - 150px); overflow-y: auto"
          height="calc(100vh - 150px)"
        >
          <el-table-column :sort-orders="['ascending', 'descending']" prop="questionName" label="Question" :min-width="getFormNameColumnMinWidth()" align="left" sortable="custom">
            <template slot-scope="scope">
              <div v-if="cloneRowIndex === scope.$index">
                <div>
                  <el-input :class="{ 'error-input': error != null }" @focus="handleFocusCloneInput" v-model="clonedFormName" size="mini" class="input-clone"></el-input>
                  <div class="error-text">{{ error }}</div>
                </div>
              </div>
              <div v-else>
                <span v-html="formatQuestionText(scope.row.questionName, scope.row)"></span>
              </div>
            </template>
          </el-table-column>
  
          <el-table-column :sort-orders="['ascending', 'descending']" prop="status" label="Status" width="220" align="left" sortable="custom">
            <template slot-scope="scope">
              <div v-if="cloneRowIndex !== scope.$index">{{ scope.row.status ? scope.row.status.charAt(0).toUpperCase() + scope.row.status.slice(1) : 'Null' }}</div>
            </template>
          </el-table-column>
  
          <el-table-column :sort-orders="['ascending', 'descending']" prop="createdDate" label="Date created" width="220" align="left" sortable="custom">
            <template slot-scope="scope">
              <div v-if="cloneRowIndex !== scope.$index">{{ scope.row.createdDate }}</div>
            </template>
          </el-table-column>
  
          <el-table-column :sort-orders="['ascending', 'descending']" prop="updatedDate" label="Date modified" width="220" align="left" sortable="custom">
            <template slot-scope="scope">
              <div v-if="cloneRowIndex !== scope.$index">{{ scope.row.updatedDate }}</div>
            </template>
          </el-table-column>
  
          <el-table-column :sort-orders="['ascending', 'descending']" prop="createdBy" label="Created by" width="220" align="left" sortable="custom">
            <template slot-scope="scope">
              <div v-if="cloneRowIndex !== scope.$index">{{ scope.row.createdBy }}</div>
            </template>
          </el-table-column>
  
          <el-table-column :sort-orders="['ascending', 'descending']" prop="updatedBy" label="Modified by" width="220" align="left" sortable="custom">
            <template slot-scope="scope">
              <div v-if="cloneRowIndex !== scope.$index">{{ scope.row.updatedBy }}</div>
            </template>
          </el-table-column>
  
          <el-table-column label="Actions" width="220" align="right">
            <template slot-scope="scope">
              <div v-if="cloneRowIndex === scope.$index" class="action-row">
                <div><el-button :disabled="clonedFormName == null || clonedFormName == ''" @click="saveClonedForm(scope.$index, scope.row)" size="mini" class="save-btn">SAVE</el-button></div>
                <el-tooltip popper-class="question-manager-page-tooltip-popper" :visible-arrow="false" effect="dark" content="Delete question" placement="bottom">
                  <div @click="cancelClone()" class="icon-size icon-delete icon-action"></div>
                </el-tooltip>
              </div>
              <div v-else>
                <!-- I'm not sure if there's a better way to refactor this - since we need to maintain the 4 column buttons, there must be a css way for this :( -->
                <div v-if="isFormRowEditable(scope.row)" class="action-row">
                  <el-tooltip v-if="isRoleAllow(4707)" popper-class="question-manager-page-tooltip-popper" :visible-arrow="false" effect="dark" content="Edit question" placement="bottom">
                    <div @click="handleEdit(scope.$index, scope.row)" class="icon-size icon-edit icon-action icon-disabled"></div>
                  </el-tooltip>
                  <el-tooltip v-else popper-class="question-manager-page-tooltip-popper" :visible-arrow="false" effect="dark" content="View question" placement="bottom">
                    <div @click="handleView(scope.$index, scope.row)" class="icon-size icon-view icon-action"></div>
                  </el-tooltip>
                  <el-tooltip popper-class="question-manager-page-tooltip-popper" :visible-arrow="false" effect="dark" content="Delete question" placement="bottom">
                    <div v-if="isRoleAllow(4707)" @click="setActionRow(scope.$index, scope.row, 'delete')" class="icon-size icon-delete icon-action"></div>
                    <div v-else class="icon-size icon-delete icon-action icon-disabled"></div>
                  </el-tooltip>
                  <el-tooltip popper-class="question-manager-page-tooltip-popper" :visible-arrow="false" effect="dark" content="Clone question" placement="bottom">
                    <div v-if="isRoleAllow(4707)" :class="['icon-size', 'icon-clone', 'icon-action', { 'icon-disabled': isRowBeingCloned() }]" @click="!isRowBeingCloned() && handleClone(scope.$index, scope.row)"></div>
                    <div v-else :class="['icon-size', 'icon-clone', 'icon-action', 'icon-disabled']"></div>
                  </el-tooltip>
                  <el-tooltip popper-class="question-manager-page-tooltip-popper" :visible-arrow="false" effect="dark" content="Publish question" placement="bottom">
                    <div v-if="isRoleAllow(4707)" @click="handlePublish(scope.row)" class="icon-size icon-publish icon-action"></div>
                    <div v-else class="icon-size icon-publish icon-action icon-disabled"></div>
                  </el-tooltip>
                </div>
  
                <span v-else-if="isFormRowPublished(scope.row)" class="action-row">
                  <div class="icon-size icon-edit icon-hidden"></div>
                  <div v-if="isFormRowInTask(scope.row)" class="icon-size icon-edit icon-hidden"></div>
                  <el-tooltip popper-class="question-manager-page-tooltip-popper" :visible-arrow="false" effect="dark" content="View question" placement="bottom">
                    <div @click="handleView(scope.$index, scope.row)" class="icon-size icon-view icon-action"></div>
                  </el-tooltip>
                  <el-tooltip popper-class="question-manager-page-tooltip-popper" :visible-arrow="false" effect="dark" content="Clone question" placement="bottom">
                    <div v-if="isRoleAllow(4707)" :class="['icon-size', 'icon-clone', 'icon-action', { 'icon-disabled': isRowBeingCloned() }]" @click="!isRowBeingCloned() && handleClone(scope.$index, scope.row)"></div>
                    <div v-else :class="['icon-size', 'icon-clone', 'icon-action', 'icon-disabled']"></div>
                  </el-tooltip>
                  <el-tooltip  popper-class="question-manager-page-tooltip-popper" :visible-arrow="false" effect="dark" content="Archive question" placement="bottom">
                    <div v-if="isRoleAllow(4707) && !isFormRowInTask(scope.row)" @click="setActionRow(scope.$index, scope.row, 'archive')" class="icon-size icon-archive icon-action"></div>
                    <div v-else class="icon-size icon-archive icon-action icon-disabled"></div>
                  </el-tooltip>
                </span>
  
                <span v-else-if="isFormRowArchived(scope.row)" class="action-row">
                  <div class="icon-size icon-edit icon-hidden"></div>
                  <div class="icon-size icon-edit icon-hidden"></div>
                  <el-tooltip popper-class="question-manager-page-tooltip-popper" :visible-arrow="false" effect="dark" content="View question" placement="bottom">
                    <div @click="handleView(scope.$index, scope.row)" class="icon-size icon-view icon-action"></div>
                  </el-tooltip>
                  <el-tooltip popper-class="question-manager-page-tooltip-popper" :visible-arrow="false" effect="dark" content="Clone question" placement="bottom">
                    <div v-if="isRoleAllow(4707)" :class="['icon-size', 'icon-clone', 'icon-action', { 'icon-disabled': isRowBeingCloned() }]" @click="!isRowBeingCloned() && handleClone(scope.$index, scope.row)"></div>
                    <div v-else :class="['icon-size', 'icon-clone', 'icon-action', 'icon-disabled']"></div>
                  </el-tooltip>
                </span>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <wy-page-modal ref="pageModal">
        <wy-evaluation-new-question :questionManager="module"></wy-evaluation-new-question>
      </wy-page-modal>
    </div>
  </template>
  
  <script>
  import Vue from "vue";
  import { mapGetters, mapActions } from "vuex";
  import WyPageModal from "../widgets/wy-page-modal";
  import WyEvaluationNewQuestion from "./wy-evaluation-new-question.vue";
  
  export default Vue.extend({
    components: {
      "wy-page-modal": WyPageModal,
      "wy-evaluation-new-question": WyEvaluationNewQuestion,
    },
    data() {
      return {
        module: this,
        isPopupVisible: false,
        popupBodyText: "",
        popupAction: "", // delete/archive
        selectedActionRow: null, // delete/archive
        currentPage: 1,
        selectedPageSize: 20,
        pageSizeOptions: [{ value: 10, label: "10" }, { value: 20, label: "20" }, { value: 40, label: "40" }, { value: 80, label: "80" }],
        paginationTotal: 0,
        tableLoading: false,
        cloneRowIndex: null,
        clonedFormName: "",
        error: null,
        toastMessage: null,
        tableData: [],
        sortedColumn: "questionName",
        sortOrder: "ascending",
        question_status_enum: {
          EDITABLE: "editable",
          PUBLISHED: "published",
          ARCHIVED: "archived",
        },
      };
    },

    computed: {
      ...mapGetters(['isRoleAllow']),
    },
  
    methods: {
      ...mapActions(["fetchAutoCustomEvalQuestionList", "deleteAutoEvalQuestion", "cloneAutoEvalQuestion", "archiveAutoEvalQuestion", 'publishAutoEvalQuestion']),

      formatQuestionText(text, question) {
        if (!text) return '';
        // Replace patterns like <@XXX> with @metadataLabel in blue color
        return text.replace(/<@([^>]+)>/g, (match, p1) => {
          const replacement = question && question.metadataLabel ? question.metadataLabel : p1;
          return `<span style="color: #1890FF;">@${replacement}</span>`;
        });
      },

      // API Methods
      async fetchQuestionManagerList() {
        this.tableLoading = true;
  
        const payload = this.getPayloadWithAdditionalFields();
        const res = await this.fetchAutoCustomEvalQuestionList(payload);
        this.handleSetTableData(res);
  
        this.tableLoading = false;
      },
  
      handleSetTableData(apiResult) {
        const error = apiResult && apiResult.data && apiResult.data.errorCode;
        if (error) return;
  
        if (apiResult && apiResult.status == 200) {
          this.tableData = apiResult.data.questions;
          this.paginationTotal = apiResult.data.total;
        }
      },
  
      handleToastMessage(apiResult, action) {
        const error = apiResult && apiResult.data && apiResult.data.errorCode
  
        if (apiResult && apiResult.status == 200) {
          this.openToast({
            isSuccess: error == null,
            action,
            message: error ? apiResult.data.message : null,
          });
        } else {
          this.openToast({
            isSuccess: false,
            action,
            message: error ? apiResult.data.message : null,
          });
        }
      },
  
      getPayloadWithAdditionalFields(additionalFields = {}) {
        return {
          pageSize: this.selectedPageSize,
          pageNumber: this.currentPage,
          sortColumn: this.sortedColumn,
          sorting: this.sortOrder === "ascending" ? "asc" : "desc",
          ...additionalFields,
        };
      },
      // End API Methods
  
      // Table/Pagination Methods
      getFormNameColumnMinWidth() {
        const MAGIC_NUMBER_FOR_WIDTH_SIZE = 8;
        let maxChar = 0;
  
        this.tableData && this.tableData.forEach((row) => {
          if (row.questionName.length > maxChar) {
            maxChar = row.questionName.length;
          }
        });
  
        return maxChar * MAGIC_NUMBER_FOR_WIDTH_SIZE;
      },
  
      async handlePageSizeChange(val) {
        this.currentPage = 1;
        this.selectedPageSize = val;
        await this.fetchQuestionManagerList();
      },
  
      async handleCurrentChange(val) {
        this.currentPage = val;
        await this.fetchQuestionManagerList();
      },

      handleDoubleRowClick(row) {
      if(row.status === this.question_status_enum.EDITABLE && this.isRoleAllow(4707)) {
        this.handleEdit(null, row);
      }
      else {
        this.handleView(null, row);
      }
    },
  
      handleSortChange({ prop, order }) {
        // Reset sorted column and order if a new column is sorted
        if (this.sortedColumn !== prop) {
          this.sortedColumn = prop;
        }
  
        if (!order) {
          const tableRef = this.$refs.filterTable;
          tableRef.sort(prop, this.sortOrder == "ascending" ? "descending" : "ascending");
          return;
        } else {
          this.sortOrder = order;
        }
  
        this.fetchQuestionManagerList();
      },
  
      isRowBeingCloned() {
        return this.cloneRowIndex !== null;
      },
  
      isFormRowPublished(row) {
        return row.status === this.question_status_enum.PUBLISHED;
      },
  
      isFormRowEditable(row) {
        return row.status === this.question_status_enum.EDITABLE;
      },
  
      isFormRowArchived(row) {
        return row.status === this.question_status_enum.ARCHIVED;
      },
  
      isFormRowInTask(row) {
        return row.inTask === "Yes";
      },
      // End Table/Pagination Methods
  
      // Action Methods
      handleView(index, row) {
        this.selectedActionRow = row;
        this.selectedActionRow.mode = "view";
        this.$root.$emit(`fire-app-modal-events`, "/autoQA/wy-evaluation-new-question.vue", this.module);
      },
  
      handleEdit(index, row) {
        this.selectedActionRow = row;
        this.selectedActionRow.mode = "edit";
        this.$root.$emit(`fire-app-modal-events`, "/autoQA/wy-evaluation-new-question.vue", this.module);
      },
  
      setActionRow(index, row, action) {
        this.isPopupVisible = true;
        this.selectedActionRow = row;
        this.popupAction = action;
  
        if (action == "delete") {
          this.popupBodyText = "Deleting a question is permanent. Deleted questions cannot be used for future evaluations. Are you sure you want to delete this question?";
        } else if (action == "archive") {
          this.popupBodyText = "Archiving a question is permanent. Archived questions cannot be used for future evaluations. Are you sure you want to archive this question?";
        }
      },
  
      resetActionRow() {
        this.isPopupVisible = false;
        this.selectedActionRow = null;
      },
  
      handlePopupConfirm() {
        if (this.popupAction == "delete") {
          this.handleDelete();
        } else if (this.popupAction == "archive") {
          this.handleArchive();
        }
      },
  
      openToast({ isSuccess, action, message }) {
        if (this.toastMessage != null) return;
  
        const failedMessage = message ? message : `We're having connection problems. Please try again.`;
  
        this.toastMessage = this.$message({
          dangerouslyUseHTMLString: false,
          duration: 3000,
          showClose: true,
          customClass: isSuccess ? "evaluation-question-success-msg" : "evaluation-question-fail-msg",
          message: isSuccess ? `Question successfully ${action.toLowerCase()}d` : `${action} failed. ${failedMessage}`,
          onClose: () => {
            this.toastMessage = null;
          },
        });
      },
  
      async handleDelete() {
        const ACTION = "Delete";
        this.tableLoading = true;
  
        const payload = this.getPayloadWithAdditionalFields({ questionId: this.selectedActionRow.questionId, status: this.selectedActionRow.status });
        this.resetActionRow();
  
        const res = await this.deleteAutoEvalQuestion({ ...payload });
        if (res.status === 409) {
          let msg = this.$t(`errorCode.code${res.data.errorCode}`, {'lockedBy': res.data.lockedBy});
          this.handleAlertMsg(msg);
        } else {
          this.handleSetTableData(res);
          this.handleToastMessage(res, ACTION);
        }
  
        this.tableLoading = false;
      },
  
      async handleArchive() {
        const ACTION = "Archive";
        this.tableLoading = true;
  
        const payload = this.getPayloadWithAdditionalFields({ questionId: this.selectedActionRow.questionId, status: this.selectedActionRow.status });
        this.resetActionRow();
  
        const res = await this.archiveAutoEvalQuestion({ ...payload });
        if (res.status === 409) {
          let msg = this.$t(`errorCode.code${res.data.errorCode}`, {'lockedBy': res.data.lockedBy});
          this.handleAlertMsg(msg);
        } else {
          this.handleSetTableData(res);
          this.handleToastMessage(res, ACTION);
        }
  
        this.tableLoading = false;
      },
  
      async handlePublish(row) {
        this.tableLoading = true;
        this.selectedActionRow = row;
  
        const payload = this.getPayloadWithAdditionalFields({ questionId: this.selectedActionRow.questionId, status: this.selectedActionRow.status });
        this.resetActionRow();
        const res = await this.publishAutoEvalQuestion({ ...payload });
        if(res.data) {
          if(res.status === 200) {
            if(this.preMsgObj == null){
              this.preMsgObj = this.$message({
                dangerouslyUseHTMLString: false,
                duration: 3000,
                showClose: true,
                customClass: 'evaluation-question-save-success-msg',
                message: 'Save Successful',
                onClose: ()=>{
                  this.preMsgObj = null;
                }
              });
            }
          }
          else if(res.status === 409) {
              let msg = this.$t(`errorCode.code${res.data.errorCode}`, {'lockedBy': res.data.lockedBy});
              this.handleAlertMsg(msg);
          }
        }
        this.handleSetTableData(res);
        this.tableLoading = false;
      },
  
      handleAlertMsg(msg) {
        if(this.preMsgObj == null) {
          this.preMsgObj = this.$message({
            dangerouslyUseHTMLString: false,
            duration: 5000,
            showClose: true,
            customClass: 'evaluation-question-publish-warning-msg',
            message: msg,
            onClose: ()=>{
              this.preMsgObj = null;
            }
          });
        }
      },
  
      handleCreateNewQuestion() {
        this.$root.$emit(`fire-app-modal-events`, "/autoQA/wy-evaluation-new-question.vue", this.module);
      },

      handleBackToFormManager() {
        this.$router.push({ name: 'aiEvaluationFormManager'});
      },
  
      handleClone(index, row) {
        this.cloneRowIndex = index;
        this.clonedFormName = `Clone of ${row.questionName}`;
      },
  
      async saveClonedForm(index, row) {
        const ACTION = "Clone";
        const newFormName = this.clonedFormName.trim();
        this.tableLoading = true;
  
        const payload = this.getPayloadWithAdditionalFields({ questionId: row.questionId, newFormName });
        const res = await this.cloneAutoEvalQuestion({ ...payload });
        if (res.status === 409) {
          let msg = this.$t(`errorCode.code${res.data.errorCode}`, {'lockedBy': res.data.lockedBy});
          this.handleAlertMsg(msg);
        } else {
          const newFormNameExistsInDB = res && (res.status == 200 && (res.data && res.data.errorCode != null));
  
          if (newFormNameExistsInDB) {
            this.error = res.data.message;
          } else {
            this.cloneRowIndex = null;
            this.clonedFormName = "";
            this.handleSetTableData(res);
          }
          this.handleToastMessage(res, ACTION);
        }
        
        this.tableLoading = false;
      },
      cancelClone() {
        // Exit clone mode without saving
        this.cloneRowIndex = null;
        this.clonedFormName = "";
        this.error = null;
      },
      handleFocusCloneInput() {
        this.error = null;
      },
      // End Action Methods
  
      // CSS Methods
      rotateCaret(isVisible) {
        const el = document.querySelector(".el-input__suffix");
        if (isVisible) {
          el.classList.add("rotate");
        } else {
          el.classList.remove("rotate");
        }
      },
  
      getHeaderClass({ column }) {
        const isChosenColumn = column.property === this.sortedColumn;
  
        if (isChosenColumn && this.sortOrder === "ascending") {
          return "ascending-caret";
        } else if (isChosenColumn && this.sortOrder === "descending") {
          return "descending-caret";
        } else {
          return "hover-caret";
        }
      },
      // End CSS Methods
    },
    async created() {
      await this.fetchQuestionManagerList();
      this.$router.app.setIFrameLoading(false);
    },
  });
  </script>
  
  <style lang="scss">
  .wy-auto-qa-ai-evaluation-question-manager-page {
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: 15px;
      border-bottom: 2px solid #eee;
      padding: 16px;
      border-bottom: 1px solid black;
  
      .title {
        font-size: 1.5rem;
        font-weight: 400;
        line-height: 1.334;
        letter-spacing: 0em;
        color: black;
      }
  
      .new-form-btns {
        display: flex;
        .new-question-btn {
          background-color: #003b5c;
          padding: 6px 16px;
    
          &:hover {
            background-color: #000;
            box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12);
            transition: all 0.3s ease;
          }
        }
    
        .new-question-btn-text {
          font-weight: 600;
          color: white;
          font-size: 14px;
        }

        .back-to-form-btn {
          background-color: #FFF;
          padding: 6px 16px;
          border-radius: 2px !important;
          border: 1px solid lightgrey;
          box-shadow: none;
          -webkit-box-shadow: unset;
          -moz-box-shadow: unset;
          -o-box-shadow: unset;
          // &:hover {
          //   background-color: #000;
          //   box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12);
          //   transition: all 0.3s ease;
          // }
        }
    
        .back-to-form-btn-text {
          // font-weight: 600;
          color: #313131;
          font-size: 14px;
        }
      }
    }
  
    .body {
      padding: 0px 10px;
    }
  
    .filter-row {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding: 10px 0px;
  
      .results-wrapper {
        display: flex;
        align-items: center;
  
        span {
          font-size: 0.875rem;
          font-weight: 400;
          line-height: 1.43;
          letter-spacing: 0.01071em;
          color: black;
          margin-right: 6px;
        }
      }
  
      .dropdown-wrapper {
        display: flex;
        align-items: center;
        margin-right: 20px;
      }
  
      .pagination-size {
        font-size: 0.875rem;
        font-family: "Roboto", "Helvetica", "Arial", sans-serif;
        font-weight: 400;
        margin-right: 10px;
        color: black;
      }
  
      .el-input--suffix .el-input__inner {
        width: 60px !important;
        border-bottom: none !important;
        color: black !important;
      }
  
      .el-select .el-input .el-select__caret {
        display: none !important;
      }
  
      .el-input__suffix {
        background-image: url("../../assets/brands/aq/icons/icon_form_manager_caret.svg");
        background-repeat: no-repeat;
        background-position: center;
        height: 24px;
        width: 24px;
        margin-top: 8px;
        opacity: 0.6;
      }
  
      // caret rotate
      .rotate {
        transform: rotate(180deg);
        transition: transform 0.3s ease;
      }
    }
  
    .input-clone {
      .el-input__inner {
        font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
        font-size: 14px !important;
        border: 1px solid #cbcbcb !important;
        border-radius: 4px !important;
        padding: 18px !important;
        color: black !important;
        background-color: transparent !important;
  
        &:focus {
          border: 2px solid #003b5c !important;
        }
      }
    }
  
    .el-table {
      font-size: 14px;
      border: 1px solid #ebeef5;
      box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12);
      height: 100%;
  
      .el-table__header-wrapper {
        background-color: #f2f2f2;
      }
  
      .el-table__body-wrapper {
        background-color: #fff;
      }
  
      tr:hover {
        background-color: #e8f3ff;
        cursor: pointer;
      }
  
      th {
        padding: 2px 8px;
        background-color: #fafafa;
        color: #313131;
        font-weight: bold;
        font-size: 14px;
      }
      th.is-sortable {
        &:hover {
          color: #7d7d7d !important;
        }
      }
    }
  
    td {
      color: black;
      padding-left: 8px;
      padding-right: 8px;
    }
  
    .cell {
      div {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  
    .caret-wrapper {
      .ascending {
        background-image: url("../../assets/brands/aq/icons/icon_form_manager_sort_asc.svg") !important;
        background-repeat: no-repeat;
        background-position: center;
        height: 24px;
        width: 24px;
        transform: rotate(180deg);
        border: none !important;
        margin-top: -1px;
        margin-left: -2px;
        opacity: 0.6;
        scale: 0.75;
        display: none;
      }
      .descending {
        background-image: url("../../assets/brands/aq/icons/icon_form_manager_sort_desc.svg") !important;
        background-repeat: no-repeat;
        background-position: center;
        height: 24px;
        width: 24px;
        border: none !important;
        margin-bottom: -1px !important;
        margin-left: -2px;
        opacity: 0.6;
        scale: 0.75;
        display: none;
      }
    }
  
    .ascending-caret {
      .caret-wrapper {
        .ascending {
          display: block !important;
        }
      }
    }
  
    .descending-caret {
      .caret-wrapper {
        .descending {
          display: block !important;
        }
      }
    }
  
    .hover-caret {
      &:hover {
        .caret-wrapper {
          .ascending {
            display: block !important;
            opacity: 0.45;
          }
        }
      }
    }
  
    .el-pagination {
      text-align: right;
  
      .el-pagination__total {
        font-size: 14px;
      }
    }
  
    .action-row {
      display: flex;
      align-items: center;
      justify-content: space-evenly;
    }
  
    .save-btn {
      background-color: white !important;
      color: black;
      box-shadow: none !important;
      border: 1px solid #ccc !important;
    }
  
    .icon-hidden {
      opacity: 0;
    }
  
    .icon-size {
      height: 24px;
      width: 24px;
    }
  
    .icon-edit {
      background-image: url("../../assets/brands/aq/icons/icon_form_manager_pencil.svg");
      background-repeat: no-repeat;
      background-position: center;
    }
  
    .icon-delete {
      background-image: url("../../assets/brands/aq/icons/icon_form_manager_trash.svg");
      background-repeat: no-repeat;
      background-position: center;
    }
  
    .icon-view {
      background-image: url("../../assets/brands/aq/icons/icon_form_manager_view.svg");
      background-repeat: no-repeat;
      background-position: center;
    }
  
    .icon-clone {
      background-image: url("../../assets/brands/aq/icons/icon_form_manager_copy.svg");
      background-repeat: no-repeat;
      background-position: center;
    }
  
    .icon-publish {
      background-image: url("../../assets/brands/aq/icons/icon_form_manager_publish.svg");
      background-repeat: no-repeat;
      background-position: center;
    }
  
    .icon-archive {
      background-image: url("../../assets/brands/aq/icons/icon_form_manager_archive.svg");
      background-repeat: no-repeat;
      background-position: center;
    }
  
    .icon-action {
      opacity: 0.85;
      cursor: pointer;
    }
  
    .icon-action:hover {
      color: #333;
    }
  
    .icon-disabled {
      opacity: 0.5;
      cursor: default;
    }
  
    .el-button {
      border-radius: 4px !important;
      padding: 12px 16px !important;
      background-color: #193b5c;
      border: none;
      box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
    }
  
    .is-disabled:hover {
      color: black !important;
    }
  
    // https://jsfiddle.net/x6pz0yhr/
    // this is the only hack that works to remove hover effect on el-table
    .no-hover:hover > td {
      background-color: initial !important;
    }
  
    .error-text {
      color: #a6182d;
      margin-left: 14px;
      font-size: 0.75rem;
      margin-top: -2px;
    }
  
    .error-input {
      border: 1px solid #a6182d !important;
      border-radius: 4px !important;
    }
  
    .popup-wrapper {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 9999;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  
    .popup-body {
      margin-top: -130px;
      background: white;
      display: flex;
      flex-direction: column;
      max-height: calc(100% - 64px);
      max-width: 600px;
      border-radius: 4px;
      font-family: "Roboto", "Helvetica", "Arial", sans-serif;
      box-shadow: 0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12);
  
      .title {
        font-size: 1.25rem;
        font-weight: 600;
        line-height: 1.6;
        letter-spacing: 0.0075em;
        padding: 16px 24px;
      }
      .desc {
        color: rgba(0, 0, 0, 0.54);
        padding: 8px 24px;
        font-size: 1rem;
        font-weight: 400;
        line-height: 1.5;
        letter-spacing: 0.00938em;
        margin-bottom: 12px;
      }
      .footer {
        padding: 8px;
        display: flex;
        justify-content: flex-end;
      }
      .el-button {
        font-weight: 600 !important;
        width: 64px;
      }
      .no-action {
        background: #e0e0e0 !important;
        color: black !important;
      }
    }
  }
  
  .question-manager-page-size-popper {
    width: 60px !important;
    margin-top: 0px !important;
    .el-select-dropdown__item {
      color: black !important;
      font-size: 1rem;
    }
    .selected {
      background-color: rgba(0, 0, 0, 0.08) !important;
      font-weight: 400 !important;
    }
  }
  
  .question-manager-page-tooltip-popper {
    padding: 6px 8px !important;
    font-size: 11px !important;
  }
  
  .el-message.evaluation-question-fail-msg {
    background-color: #a6182d;
    .el-icon-info {
      display: none;
    }
    .el-message__content {
      padding-right: 50px;
      font-weight: 700;
      color: #fff;
    }
    .el-message__closeBtn {
      color: #fff;
      font-size: 20px;
      font-weight: 700;
    }
  }
  
  .el-message.evaluation-question-success-msg {
    background-color: #4caf50;
    .el-icon-info {
      display: none;
    }
    .el-message__content {
      padding-right: 50px;
      font-weight: 700;
      color: #fff;
    }
    .el-message__closeBtn {
      color: #fff;
      font-size: 20px;
      font-weight: 700;
    }
  }
  
  .el-message.evaluation-question-duplicate-name-warning-msg, .el-message.evaluation-question-internal-error-warning-msg, .evaluation-question-publish-warning-msg {
    background-color: #a6182d;
    padding: 15px 35px 15px 20px;
    .el-icon-info {
      display: none;
    }
    .el-message__content {
      padding-right: 50px;
      font-weight: 700;
      color: #FFF;
    }
    .el-message__closeBtn {
      color: #FFF;
      font-size: 20px;
      font-weight: 700;
    }
  }
  
  .el-message.evaluation-question-save-success-msg {
    background-color: #4caf50;
    .el-icon-info {
      display: none;
    }
    .el-message__content {
      padding-right: 50px;
      font-weight: 700;
      color: #FFF;
    }
    .el-message__closeBtn {
      color: #FFF;
      font-size: 20px;
      font-weight: 700;
    }
  }
  </style>
  